import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, Spinner } from "reactstrap";
import { injectIntl } from "react-intl";
import I18nProvider from "../../services/I18nProvider";
import Messages from "../../Shared/Messages";
import "./MPSTypeaheadSelect.scss";

const SYSTEM_LABELS = {
  "http://snomed.info/sct": "SNOMED",
  "http://hl7.org/fhir/sid/icd-10": "ICD-10",
  "http://hl7.org/fhir/sid/icd-11": "ICD-11",
  "http://loinc.org": "LOINC",
};

const MPSTypeaheadSelect = ({
  searchFn,
  value,
  onChange,
  placeholder,
  multi = false,
  disabled = false,
  count = 15,
  minChars = 2,
  debounceMs = 300,
  showSystem = false,
  showInactive = false,
  eagerLoad = false,
  error,
  fallbackToTextInput = true,
  onInputChange: onInputChangeProp,
  intl,
}) => {
  const [inputValue, setInputValue] = useState(
    !multi && value && value.display ? value.display : ""
  );
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [allResults, setAllResults] = useState(null); // Pre-loaded results for eagerLoad
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [serverError, setServerError] = useState(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const optionsListRef = useRef(null);
  // Stable ref for searchFn — parent may recreate the function on every render
  // (e.g. ECL arrow functions from getSearchFnForLookup), so we use a ref to
  // prevent the eagerLoad useEffect from re-firing and reopening the dropdown.
  const searchFnRef = useRef(searchFn);
  searchFnRef.current = searchFn;

  const isRTL = I18nProvider.isRTLLocale();

  // Eager load: fetch all results on mount
  useEffect(() => {
    if (!eagerLoad || !searchFnRef.current) return;
    let cancelled = false;
    const fetchAll = async () => {
      setIsLoading(true);
      setServerError(null);
      const response = await searchFnRef.current("", 200);
      if (cancelled) return;
      if (response.success === false) {
        const errMsg = response.errorMessage;
        setServerError(
          typeof errMsg === "string" && errMsg
            ? errMsg
            : intl.formatMessage(Messages.terminology_server_unavailable)
        );
        setAllResults([]);
      } else {
        const codes = response.codes || [];
        const filtered = showInactive
          ? codes
          : codes.filter((c) => !c.inactive);
        setAllResults(filtered);
        setResults(filtered);
        setTotal(response.total || 0);
        // Auto-open dropdown once results are loaded
        if (filtered.length > 0) setIsOpen(true);
      }
      setIsLoading(false);
    };
    fetchAll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eagerLoad, showInactive, intl]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Reset highlight when results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [results]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightIndex >= 0 && optionsListRef.current) {
      const options = optionsListRef.current.querySelectorAll(
        ".mps-typeahead-select-option"
      );
      if (options[highlightIndex]) {
        options[highlightIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightIndex]);

  const doSearch = useCallback(
    async (text) => {
      // Eager mode: filter client-side from pre-loaded results
      if (eagerLoad && allResults) {
        const query = (text || "").toLowerCase();
        const filtered = query
          ? allResults.filter(
              (c) =>
                c.display.toLowerCase().includes(query) ||
                c.code.toLowerCase().includes(query)
            )
          : allResults;
        setResults(filtered);
        setTotal(filtered.length);
        setIsOpen(filtered.length > 0);
        return;
      }

      if (!text || text.length < minChars) {
        setResults([]);
        setTotal(0);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      setServerError(null);

      const response = await searchFnRef.current(text, count);

      if (response.success === false) {
        const errMsg = response.errorMessage;
        setServerError(
          typeof errMsg === "string" && errMsg
            ? errMsg
            : intl.formatMessage(Messages.terminology_server_unavailable)
        );
        setResults([]);
        setTotal(0);
      } else {
        const codes = response.codes || [];
        const filtered = showInactive
          ? codes
          : codes.filter((c) => !c.inactive);
        setResults(filtered);
        setTotal(response.total || 0);
        setIsOpen(filtered.length > 0);
      }

      setIsLoading(false);
    },
    // searchFn accessed via searchFnRef to avoid re-creating this callback on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [count, minChars, showInactive, intl, eagerLoad, allResults]
  );

  const handleInputChange = (e) => {
    let text = e.target.value;

    // In multi mode, treat comma as a separator — discard everything before it
    // and start a fresh search with what comes after
    if (multi && text.includes(",")) {
      text = text.split(",").pop().trimStart();
    }

    setInputValue(text);
    setHasUserEdited(true);
    if (onInputChangeProp) onInputChangeProp(text);

    if (eagerLoad && allResults) {
      // Client-side filter — no debounce needed
      doSearch(text);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      doSearch(text);
    }, debounceMs);
  };

  const handleSelect = (item) => {
    const selected = {
      code: item.code,
      display: item.display,
      system: item.system,
      translations: item.translations,
    };

    if (multi) {
      const current = Array.isArray(value) ? value : [];
      const alreadySelected = current.some((v) => v.code === item.code);
      if (!alreadySelected) {
        onChange([...current, selected]);
      }
      setInputValue("");
      setHasUserEdited(false);
    } else {
      onChange(selected);
      setInputValue(item.display);
      setHasUserEdited(false);
    }

    setIsOpen(false);
    setResults([]);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < results.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : results.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < results.length) {
        handleSelect(results[highlightIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  const handleRemoveTag = (code) => {
    if (multi && Array.isArray(value)) {
      onChange(value.filter((v) => v.code !== code));
    }
  };

  const getSystemLabel = (system) => {
    return SYSTEM_LABELS[system] || system;
  };

  // Display value for single select
  // Once the user has started editing, respect their input (even if empty)
  const displayInputValue = multi
    ? inputValue
    : hasUserEdited
      ? inputValue
      : inputValue || (value && value.display) || "";

  const resultCountText =
    total > results.length
      ? intl.formatMessage(Messages.terminology_showing_results, {
          shown: results.length,
          total: total,
        })
      : `${results.length} ${results.length === 1 ? "result" : "results"}`;

  return (
    <div
      className="mps-typeahead-select-container"
      ref={containerRef}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Multi-select tags */}
      {multi && Array.isArray(value) && value.length > 0 && (
        <div className="mps-typeahead-select-tags">
          {value.map((v) => (
            <span key={v.code} className="mps-typeahead-select-tag">
              {v.display}
              <span
                className="mps-typeahead-select-tag-remove"
                onClick={() => handleRemoveTag(v.code)}
              >
                &times;
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="mps-typeahead-select-input-wrapper">
        <Input
          type="text"
          value={displayInputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (eagerLoad && allResults) {
              // Show all (or filtered) results immediately on focus
              doSearch(inputValue);
            } else if (results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder={
            placeholder ||
            (eagerLoad ? "Select or type to filter..." : intl.formatMessage(Messages.terminology_search_placeholder))
          }
          disabled={disabled}
          className={error ? "is-invalid" : ""}
        />
        {isLoading && (
          <span className="mps-typeahead-select-loading">
            <Spinner size="sm" />
          </span>
        )}
      </div>

      {/* Navigate hint bar — sits between input and dropdown */}
      {isOpen && results.length > 0 && (
        <div className="mps-typeahead-select-nav-hint" onMouseDown={(e) => e.preventDefault()}>
          <span className="mps-typeahead-select-nav-count">
            {highlightIndex >= 0
              ? `${highlightIndex + 1} of ${results.length}`
              : resultCountText}
          </span>
          <span className="mps-typeahead-select-nav-keys">
            <kbd
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => {
                e.preventDefault();
                setHighlightIndex((prev) => prev > 0 ? prev - 1 : results.length - 1);
              }}
            >&uarr;</kbd>
            <kbd
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => {
                e.preventDefault();
                setHighlightIndex((prev) => prev < results.length - 1 ? prev + 1 : 0);
              }}
            >&darr;</kbd>
            <kbd
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => {
                e.preventDefault();
                if (highlightIndex >= 0 && highlightIndex < results.length) {
                  handleSelect(results[highlightIndex]);
                }
              }}
            >&crarr;</kbd>
            <span className="mps-typeahead-select-nav-label">Navigate</span>
          </span>
        </div>
      )}

      {/* Dropdown options */}
      {isOpen && (
        <div className="mps-typeahead-select-dropdown" ref={optionsListRef}>
          {results.length === 0 && !isLoading && (
            <div className="mps-typeahead-select-message">
              {intl.formatMessage(Messages.terminology_no_results)}
            </div>
          )}

          {results.map((item, index) => (
            <div
              key={`${item.system}-${item.code}`}
              className={`mps-typeahead-select-option${
                item.inactive
                  ? " mps-typeahead-select-option--inactive"
                  : ""
              }${
                index === highlightIndex
                  ? " mps-typeahead-select-option--highlighted"
                  : ""
              }`}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              <div>
                <span className="mps-typeahead-select-option-display">
                  {item.display}
                </span>
                <span className="mps-typeahead-select-option-code">
                  {item.code}
                </span>
                {item.inactive && (
                  <span className="mps-typeahead-select-inactive-badge">
                    {intl.formatMessage(Messages.terminology_inactive_code)}
                  </span>
                )}
              </div>
              {showSystem && item.system && (
                <span className="mps-typeahead-select-option-system">
                  {getSystemLabel(item.system)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Min chars hint */}
      {!eagerLoad && inputValue.length > 0 && inputValue.length < minChars && !isOpen && (
        <div className="mps-typeahead-select-message">
          {intl.formatMessage(Messages.terminology_min_chars, {
            min: minChars,
          })}
        </div>
      )}

      {/* Server error */}
      {serverError && (
        <div className="mps-typeahead-select-error">{serverError}</div>
      )}

      {/* Validation error */}
      {error && <div className="mps-typeahead-select-error">{error}</div>}
    </div>
  );
};

export default injectIntl(MPSTypeaheadSelect);
